

import React, { useState, useEffect } from 'react';
import Parse from 'parse';

import useUIPresentationMode from '../helpers/useUIPresentationMode.js';

import '../styles/SideBarLeft.css';
import '../styles/SideBarToggle.css';

import PlanHubLogo from '../../images/PlanHubLogoLighter.png';
import IconLayers from '../../images/IconLayers@3x.png';
import IconLayersActive from '../../images/IconLayersActive@3x.png';
import IconPlans from '../../images/IconPlans@3x.png';
import IconPlansActive from '../../images/IconPlansActive@3x.png';
import CollapseIconHover from '../../images/IconLeftArrowBlue@3x.png';
import CollapseIcon from '../../images/IconLeftArrowGrey@3x.png';
import IconClear from '../../images/IconClear@3x.png';
import IconClearBlue from '../../images/IconClearBlue@3x.png';


const SideBarLeft = ({
  onLogout,
  onBaseMapChange,
  layers,
  selectedBaseMap,
  isParcelBoundariesVisible,
  toggleParcelBoundaries,
  plansBySeries,
  selectedPlanId,
  activeOverlayPlanId,
  isVisible,
  onRequestClose,
  onPlanSelect,
  clearOverlayOnly,
}) => {


  const urlParams = new URLSearchParams(window.location.search);
  const hasPlanParam = urlParams.has('p');

  const [layersExpanded, setLayersExpanded] = useState(false);
  const [plansExpanded, setPlansExpanded] = useState(hasPlanParam);
  const [expandedSeries, setExpandedSeries] = useState({});  // For the collapsable series lists.

  const [userName, setUserName] = useState('');

  const [isSideBarCollapseButtonHovering, setIsSideBarCollapseButtonHovering] = useState(false);
  const [hoveredClearOverlayPlanId, setHoveredClearOverlayPlanId] = useState(null);

  const { presentationModeSize } = useUIPresentationMode();
  const hideLogo = presentationModeSize === 5;


  /*-----------------------------------------------------------------------------*/


  useEffect(() => {

    if (!activeOverlayPlanId || !plansBySeries || Object.keys(plansBySeries).length === 0) return;

    for (const [seriesName, plans] of Object.entries(plansBySeries)) {

      if (plans.some(plan => plan.id === activeOverlayPlanId)) {

        setExpandedSeries(prev => ({

          ...prev,
          [seriesName]: true,
        }));
        break;
      }
    }
  }, [activeOverlayPlanId, plansBySeries]);


  // This alernative version collapses all other series when one is expanded.
  const toggleSeries = (seriesName) => {
    setExpandedSeries(prev => {
      const currentlyExpanded = prev[seriesName];
      // Collapse all others, expand this one if it wasn’t already
      return currentlyExpanded
        ? {} // collapse all
        : { [seriesName]: true }; // expand only this
    });
  };


  const handleBaseMapChange = (event) => {
    const newBaseMap = event.target.value;
    //console.log("Base map changed to:", newBaseMap);
    onBaseMapChange(newBaseMap);
  };


  // Show the user's name above the logout button.
  useEffect(() => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      const fullName = `${currentUser.get('firstName')} ${currentUser.get('lastName')}`;
      setUserName(fullName);
    }
  }, []);


  // Add a useEffect() in SideBarLeft.js to reset the hover state whenever selectedPlanId changes (i.e., when the overlay is cleared or re-applied):
  useEffect(() => {
    setHoveredClearOverlayPlanId(null);
  }, [selectedPlanId]);


  if (!layers.primary || !layers.secondary) {
    //return <div>Loading...</div>;
  }


  /*-----------------------------------------------------------------------------*/


  return (

    <div
      className="sidebar"
      style={{
        width: '100%',
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: isVisible ? '0.75px solid #596A99' : 'none',
        transition: 'flex-basis 0.3s ease, opacity 0.3s ease',
        opacity: isVisible ? 1 : 0,
      }}
    >

      {/* Minimise toggle */}
      <button
        className="sidebar-toggle-button sidebar-left-toggle-button-expanded"
        onClick={onRequestClose}
        onMouseEnter={() => setIsSideBarCollapseButtonHovering(true)}
        onMouseLeave={() => setIsSideBarCollapseButtonHovering(false)}
      >
        <img
          src={isSideBarCollapseButtonHovering ? CollapseIconHover : CollapseIcon}
          alt="Hide Sidebar"
        />
      </button>

      {/* Scrollable content */}
      <div className="sidebar-inner">

        {!hideLogo && (
          <div className="sidebar-logo-container">
            <img
              src={PlanHubLogo}
              alt="PlanHub Logo"
              className="sidebar-logo"
            />
          </div>
        )}

        {isVisible && (
          <>

            {/* Layers Section */}
            <div className="section">
              <div
                className="sidebar-heading-row"
                onClick={() => setLayersExpanded(prev => !prev)}
                title="Toggle Layers Section"
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={layersExpanded ? IconLayersActive : IconLayers}
                  alt="Layers Icon"
                  className="sidebar-icon-left"
                />
                <span
                  className={`sidebar-heading-label ${layersExpanded ? 'active' : 'inactive'}`}
                >
                  Layers
                </span>
              </div>

              <div className={`collapsible-section ${layersExpanded ? 'expanded' : ''}`}>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={isParcelBoundariesVisible}
                    onChange={(e) => toggleParcelBoundaries(e.target.checked)}
                  />
                  <span>Parcel Boundaries</span>
                </label>

                {layers.secondary.map((layer) => (
                  <label className="radio" key={layer.id}>
                    <input
                      type="radio"
                      name="basemap"
                      value={layer.get('name')}
                      checked={selectedBaseMap === layer.get('name')}
                      onChange={handleBaseMapChange}
                    />
                    <span>{layer.get('name')}</span>
                  </label>
                ))}

                <label className="radio">
                  <input
                    type="radio"
                    name="basemap"
                    value="None"
                    checked={selectedBaseMap === 'None'}
                    onChange={handleBaseMapChange}
                  />
                  <span>Road Map</span>
                </label>
              </div>
            </div>


            {/* Plans Section */}
            <div className="section">
              <div
                className="sidebar-heading-row"
                onClick={() => setPlansExpanded(prev => !prev)}
                title="Toggle Plans Section"
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={plansExpanded ? IconPlansActive : IconPlans}
                  alt="Plans Icon"
                  className="sidebar-icon-left"
                />
                <span
                  className={`sidebar-heading-label ${plansExpanded ? 'active' : 'inactive'}`}
                >
                  Plans
                </span>
              </div>


              <div className={`collapsible-section ${plansExpanded ? 'expanded' : ''}`}>
                <div className="plans-list">
                  {Object.keys(plansBySeries).length === 0 ? (
                    <p>Loading plans...</p>
                  ) : (
                    Object.entries(plansBySeries).map(([seriesName, plans]) => {
                      const isExpanded = expandedSeries[seriesName] ?? false;

                      return (
                        <div key={seriesName} className="plan-series">
                          <h3
                            className="series-header collapsible-header"
                            onClick={() => toggleSeries(seriesName)}
                          >
                            {seriesName} {isExpanded ? '▾' : '▸'}
                          </h3>
                          <div
                            className={`plan-items ${isExpanded ? 'expanded' : ''}`}
                            style={{ maxHeight: isExpanded ? '1000px' : '0px' }}
                          >

                            {plans.map(plan => (
                              <label className="radio" key={plan.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="radio"
                                  name="selectedPlan"
                                  value={plan.id}
                                  checked={activeOverlayPlanId === plan.id}
                                  onChange={() => onPlanSelect(plan)}
                                />
                                <span style={{ flexGrow: 1 }}>{plan.get('name') || 'Unnamed Plan'}</span>

                                {selectedPlanId === plan.id && (
                                  <img
                                    src={hoveredClearOverlayPlanId === plan.id ? IconClearBlue : IconClear}
                                    alt="Clear Overlay"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      clearOverlayOnly?.();
                                    }}
                                    style={{
                                      height: '16px',
                                      width: '16px',
                                      cursor: 'pointer',
                                      borderRadius: '50%',
                                      transition: 'box-shadow 0.2s ease',
                                      boxShadow:
                                        hoveredClearOverlayPlanId === plan.id
                                          ? 'var(--ph-box-shadow-hover)'
                                          : 'none',
                                      marginRight: '12px'
                                    }}
                                    onMouseEnter={() => setHoveredClearOverlayPlanId(plan.id)}
                                    onMouseLeave={() => setHoveredClearOverlayPlanId(null)}
                                  />
                                )}
                              </label>
                            ))}

                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>


      {/* Sticky Footer */}
      {isVisible && (
        <div className="sidebar-footer">

          {userName && (
            <div className="logout-button" style={{ cursor: 'default', pointerEvents: 'none' }}>
              {userName}
            </div>
          )}

          <button className="logout-button" onClick={onLogout}>
            Log Out
          </button>

          <div className="copyright">© PlanHub 2026</div>

        </div>
      )}
    </div>
  );

};


export default SideBarLeft;


